import { domainError, type DomainError } from '../shared/domain-error';
import { normalizeName } from '../shared/naming';
import { err, ok, type Result } from '../shared/result';
import type { PileCapTypeId, StructuralPieceId, UnitTypeId } from '../shared/ids';
import { roundQuantity } from '../schedule/unit-of-measure';
import type { ItemFactor } from './own-factor';
import type { SteelRate } from './steel';
import { pieceNameTooLong, pieceNameTooLongMessage } from './pile';
import {
  exactPolygonArea,
  isSimplePolygon,
  isValidOutline,
  polygonArea,
  polygonPerimeter,
  type Point,
} from './polygon';

/**
 * O bloco e a viga baldrame (Fase 27, etapa 2 — D3, D6, F1, F3, F14).
 *
 * São peças da mesma família da estaca (ADR-175): mesma tabela (`structural_pieces`, com o
 * `kind` dizendo qual), mesmo dono — a área comum do desenho da Fundação ou da Contenção
 * (RN-361) — e nenhuma delas entra nas medidas de AMBIENTE, PAREDE, PILAR ou VIGA (RN-360).
 * No rascunho cada uma tem a sua lista, como o ambiente, a parede e o shaft.
 *
 * > **A regra em uma linha (RN-368):** o baldrame é desenhado de eixo a eixo dos blocos, e o
 * > trecho que passa dentro de um bloco sai da conta — o concreto dali já é do bloco.
 */

export const FOUNDATION_ERROR = {
  NAME_REQUIRED: 'TAKEOFF.FOUNDATION_NAME_REQUIRED',
  NAME_TAKEN: 'TAKEOFF.FOUNDATION_NAME_TAKEN',
  NAME_TOO_LONG: 'TAKEOFF.FOUNDATION_NAME_TOO_LONG',
  INVALID_OUTLINE: 'TAKEOFF.FOUNDATION_INVALID_OUTLINE',
  INVALID_DIMENSION: 'TAKEOFF.FOUNDATION_INVALID_DIMENSION',
  ZERO_LENGTH: 'TAKEOFF.FOUNDATION_ZERO_LENGTH',
} as const;

// ── O bloco ─────────────────────────────────────────────────────────────────────

/**
 * O bloco — o contorno em planta e a altura (D3).
 *
 * Retângulo por dois cantos ou polígono livre, como o ambiente: o bloco de três estacas não
 * é retângulo. O contorno é guardado em metros, nas coordenadas da tipologia, como o do
 * ambiente; a altura é a do concreto, do fundo ao topo.
 */
export interface PileCap {
  readonly id: StructuralPieceId;
  readonly unitTypeId: UnitTypeId;
  readonly kind: 'BLOCO';
  /** "B14" — o nome do projeto de fundação. Único entre os blocos do desenho. */
  readonly name: string;
  readonly outline: readonly Point[];
  /** Metros. */
  readonly height: number;
  /** O tipo de bloco (Fase 41, RN-537): a composição dele. Ausente: o bloco segue a obra. */
  readonly pileCapTypeId?: PileCapTypeId;
  /**
   * O fator próprio de cada item neste bloco, por cima do tipo (Fase 41, RN-538). Ausente: o
   * rascunho de antes, que não o conhece; vazio: nenhum.
   */
  readonly factors?: readonly ItemFactor[];
  /**
   * A taxa de aço própria desta peça, kg/m³ por bitola (Fase 41, etapa 2 — RN-544). Vale inteira,
   * por cima da do tipo e da disciplina. Ausente: o rascunho de antes; vazia: nenhuma.
   */
  readonly steelRates?: readonly SteelRate[];
  readonly notes?: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly deletedAt?: string;
}

// ── A viga baldrame ─────────────────────────────────────────────────────────────

/**
 * A viga baldrame — o eixo e a seção (D6).
 *
 * Dois cliques, de eixo a eixo dos blocos, e a seção como a obra fala: "20 × 40" é largura
 * 0,20 e altura 0,40. A conta tira o trecho dentro dos blocos (`groundBeamFreeLength`).
 */
export interface GroundBeam {
  readonly id: StructuralPieceId;
  readonly unitTypeId: UnitTypeId;
  readonly kind: 'BALDRAME';
  /** "VB1" — único entre os baldrames do desenho. */
  readonly name: string;
  readonly from: Point;
  readonly to: Point;
  /** Metros — o primeiro número de "20 × 40". */
  readonly width: number;
  /** Metros — o segundo. */
  readonly height: number;
  /** O fator próprio de cada item nesta viga, por cima do índice da obra (Fase 41, RN-538). */
  readonly factors?: readonly ItemFactor[];
  /**
   * A taxa de aço própria desta peça, kg/m³ por bitola (Fase 41, etapa 2 — RN-544). Vale inteira,
   * por cima da do tipo e da disciplina. Ausente: o rascunho de antes; vazia: nenhuma.
   */
  readonly steelRates?: readonly SteelRate[];
  readonly notes?: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly deletedAt?: string;
}

// ── As conferências ─────────────────────────────────────────────────────────────

function positivo(valor: number): boolean {
  return Number.isFinite(valor) && valor > 0;
}

function tres(valor: number): number {
  return Math.round(valor * 1000) / 1000;
}

/** O nome, conferido: obrigatório e único entre as peças **do mesmo tipo** no desenho. */
function nomeDaPeca(
  rotulo: string,
  name: string,
  siblings: readonly {
    readonly id: StructuralPieceId;
    readonly name: string;
    readonly deletedAt?: string;
  }[],
  selfId: StructuralPieceId | undefined,
): Result<string, DomainError> {
  const nome = name.trim();
  if (nome === '') {
    return err(domainError(FOUNDATION_ERROR.NAME_REQUIRED, `O nome do ${rotulo} é obrigatório.`));
  }
  if (pieceNameTooLong(nome)) {
    return err(domainError(FOUNDATION_ERROR.NAME_TOO_LONG, pieceNameTooLongMessage(rotulo)));
  }
  const chave = normalizeName(nome);
  if (
    siblings.some(
      (outra) =>
        outra.deletedAt === undefined && outra.id !== selfId && normalizeName(outra.name) === chave,
    )
  ) {
    return err(
      domainError(FOUNDATION_ERROR.NAME_TAKEN, `Já existe o ${rotulo} ${nome} neste desenho.`, {
        name: nome,
      }),
    );
  }
  return ok(nome);
}

export function checkPileCapFields(
  fields: { readonly name: string; readonly outline: readonly Point[]; readonly height: number },
  siblings: readonly Pick<PileCap, 'id' | 'name' | 'deletedAt'>[],
  selfId?: StructuralPieceId,
): Result<
  { readonly name: string; readonly outline: readonly Point[]; readonly height: number },
  DomainError
> {
  const nome = nomeDaPeca('bloco', fields.name, siblings, selfId);
  if (!nome.ok) return nome;
  // `isValidOutline` confere pontos e lados; três pontos em linha reta passariam por ele e
  // dariam um bloco de área zero — concreto nenhum, e uma fôrma que existe.
  if (isValidOutline(fields.outline) && !isSimplePolygon(fields.outline)) {
    return err(
      domainError(
        FOUNDATION_ERROR.INVALID_OUTLINE,
        `O contorno se cruza: trace o bloco ${nome.value} de novo, sem lados que se atravessem.`,
      ),
    );
  }
  if (!isValidOutline(fields.outline) || polygonArea(fields.outline) < 0.0001) {
    return err(
      domainError(
        FOUNDATION_ERROR.INVALID_OUTLINE,
        `O contorno do bloco ${nome.value} precisa de ao menos três pontos e área maior que zero.`,
      ),
    );
  }
  if (!positivo(fields.height)) {
    return err(
      domainError(
        FOUNDATION_ERROR.INVALID_DIMENSION,
        `A altura do bloco ${nome.value} deve ser maior que zero.`,
        { height: fields.height },
      ),
    );
  }
  return ok({ name: nome.value, outline: fields.outline, height: tres(fields.height) });
}

export function checkGroundBeamFields(
  fields: {
    readonly name: string;
    readonly from: Point;
    readonly to: Point;
    readonly width: number;
    readonly height: number;
  },
  siblings: readonly Pick<GroundBeam, 'id' | 'name' | 'deletedAt'>[],
  selfId?: StructuralPieceId,
): Result<
  {
    readonly name: string;
    readonly from: Point;
    readonly to: Point;
    readonly width: number;
    readonly height: number;
  },
  DomainError
> {
  const nome = nomeDaPeca('baldrame', fields.name, siblings, selfId);
  if (!nome.ok) return nome;
  if (distancia(fields.from, fields.to) < 0.01) {
    return err(
      domainError(
        FOUNDATION_ERROR.ZERO_LENGTH,
        `O baldrame ${nome.value} precisa de dois pontos diferentes.`,
      ),
    );
  }
  if (!positivo(fields.width) || !positivo(fields.height)) {
    return err(
      domainError(
        FOUNDATION_ERROR.INVALID_DIMENSION,
        `A seção do baldrame ${nome.value} deve ter largura e altura maiores que zero.`,
        { width: fields.width, height: fields.height },
      ),
    );
  }
  return ok({
    name: nome.value,
    from: fields.from,
    to: fields.to,
    width: tres(fields.width),
    height: tres(fields.height),
  });
}

// ── As contas do bloco ──────────────────────────────────────────────────────────

/** A área em planta — o fundo: compactação e lastro. m², duas casas. */
export function pileCapBottom(cap: Pick<PileCap, 'outline'>): number {
  return roundQuantity(polygonArea(cap.outline), 2);
}

/** O concreto: área × altura — da área exata, arredondando só o resultado. m³, três casas. */
export function pileCapVolume(cap: Pick<PileCap, 'outline' | 'height'>): number {
  return roundQuantity(exactPolygonArea(cap.outline) * cap.height, 3);
}

/** As faces laterais: perímetro × altura — a fôrma e a impermeabilização. m², duas casas. */
export function pileCapSideFace(cap: Pick<PileCap, 'outline' | 'height'>): number {
  return roundQuantity(polygonPerimeter(cap.outline) * cap.height, 2);
}

// ── As contas do baldrame ───────────────────────────────────────────────────────

function distancia(a: Point, b: Point): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

/**
 * O ponto está dentro do polígono? (par-ímpar)
 *
 * O ponto exatamente na borda pode cair de qualquer lado — e não importa aqui: quem pergunta
 * é o meio de um trecho entre dois cruzamentos, e o meio nunca está na borda.
 */
export function pointInPolygon(point: Point, outline: readonly Point[]): boolean {
  let dentro = false;
  for (let i = 0, j = outline.length - 1; i < outline.length; j = i++) {
    const a = outline[i]!;
    const b = outline[j]!;
    if (a.y > point.y !== b.y > point.y) {
      const x = ((b.x - a.x) * (point.y - a.y)) / (b.y - a.y) + a.x;
      if (point.x < x) dentro = !dentro;
    }
  }
  return dentro;
}

/**
 * Os trechos do segmento que passam dentro do polígono, em fração do segmento (0 a 1).
 *
 * Corta o segmento em todo cruzamento com as arestas e pergunta, trecho a trecho, se o meio
 * está dentro. Serve ao bloco de qualquer forma, convexo ou não.
 */
function trechosDentro(from: Point, to: Point, outline: readonly Point[]): [number, number][] {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const cortes = [0, 1];
  for (let i = 0; i < outline.length; i++) {
    const a = outline[i]!;
    const b = outline[(i + 1) % outline.length]!;
    const ex = b.x - a.x;
    const ey = b.y - a.y;
    const denominador = dx * ey - dy * ex;
    if (Math.abs(denominador) < 1e-12) continue;
    const t = ((a.x - from.x) * ey - (a.y - from.y) * ex) / denominador;
    const u = ((a.x - from.x) * dy - (a.y - from.y) * dx) / denominador;
    if (t > 0 && t < 1 && u >= 0 && u <= 1) cortes.push(t);
  }
  cortes.sort((p, q) => p - q);
  const dentro: [number, number][] = [];
  for (let i = 0; i < cortes.length - 1; i++) {
    const [inicio, fim] = [cortes[i]!, cortes[i + 1]!];
    if (fim - inicio < 1e-9) continue;
    const meio = (inicio + fim) / 2;
    if (pointInPolygon({ x: from.x + dx * meio, y: from.y + dy * meio }, outline)) {
      dentro.push([inicio, fim]);
    }
  }
  return dentro;
}

/**
 * O comprimento livre do baldrame: o eixo menos o que passa dentro dos blocos (RN-368).
 *
 * A ponta sem bloco não corta nada — o baldrame apoiado direto na estaca conta de eixo a eixo
 * (F14). Os trechos de blocos diferentes que se sobrepõem são unidos antes de descontar: dois
 * blocos encostados não tiram o mesmo pedaço duas vezes. Metros, duas casas.
 */
export function groundBeamFreeLength(
  beam: Pick<GroundBeam, 'from' | 'to'>,
  caps: readonly Pick<PileCap, 'outline' | 'deletedAt'>[],
): number {
  const total = distancia(beam.from, beam.to);
  const trechos = caps
    .filter((cap) => cap.deletedAt === undefined && isValidOutline(cap.outline))
    .flatMap((cap) => trechosDentro(beam.from, beam.to, cap.outline))
    .sort((p, q) => p[0] - q[0]);
  let coberto = 0;
  let ate = 0;
  for (const [inicio, fim] of trechos) {
    const de = Math.max(inicio, ate);
    if (fim > de) coberto += fim - de;
    ate = Math.max(ate, fim);
  }
  return roundQuantity(total * (1 - coberto), 2);
}

/** O concreto do trecho livre: largura × altura × comprimento livre. m³, três casas. */
export function groundBeamVolume(
  beam: Pick<GroundBeam, 'width' | 'height'>,
  freeLength: number,
): number {
  return roundQuantity(beam.width * beam.height * freeLength, 3);
}

/** As duas faces laterais do trecho livre — a fôrma. m², duas casas. */
export function groundBeamSideFace(beam: Pick<GroundBeam, 'height'>, freeLength: number): number {
  return roundQuantity(2 * beam.height * freeLength, 2);
}

/** O fundo do trecho livre — compactação e lastro. m², duas casas. */
export function groundBeamBottom(beam: Pick<GroundBeam, 'width'>, freeLength: number): number {
  return roundQuantity(beam.width * freeLength, 2);
}
